<template>
  <div class="tournaments">
    <div class="tournaments__top-wrapper">
      <div class="image-wrapper">
        <img
          alt="trophy"
          class="tournaments-card__image"
          src="/img/trophy.png"
        >
      </div>
      <div class="tournaments__info">
        <h1 class="tournaments__info-title">
          {{ t('tournaments') }}
        </h1>
        <p class="tournaments__info-desc">
          {{ t('tournaments_desc') }}
        </p>
      </div>
    </div>

    <div class="tournaments__sort f">
      <span class="f14 light">
        {{ props.items?.length }} {{ t('tournaments').toLowerCase() }}
      </span>

      <ui-select
        v-model="sort"
        :items="[
          { label: t('sort_oldest_to_newest'), value: TournamentYearSort.Asc },
          { label: t('sort_newest_to_oldest'), value: TournamentYearSort.Desc },
        ]"
        label-key="label"
        value-key="value"
      />
    </div>

    <div
      v-if="props.items?.length && !loading"
      class="tournaments__grid"
    >
      <TournamentCard
        v-for="item in props.items"
        :key="item.name"
        :item="item"
      />
    </div>
    <div v-else-if="loading">
      <div class="f fc">
        <i class="i-loading ml10" />
      </div>
    </div>
    <LazyUiNoResult
      v-else
      class="mxa"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Tournament } from '~/graphql/generated/schema-types';
import TournamentCard from '~/components/Pages/Tounaments/Components/TournamentCard.vue';
import { TournamentYearSort } from '~/graphql/generated/schema-types';

const props = defineProps<{
  items: Tournament[]
  loading: boolean
}>();
const sort = defineModel('sort');

const { t } = useI18n();
</script>

<style lang="scss" scoped>
.tournaments {
  padding-top: 32px;
  padding-bottom: 80px;

  &__top-wrapper {
    display: flex;
    align-items: center;
    gap: 56px;
    margin-bottom: 40px;

    .image-wrapper {
      width: 200px;
      height: 200px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        position: relative;
        z-index: 1;
      }

      &:after {
        content: '\e81f';
        font-family: 'fontello';
        position: absolute;
        inset: 0;
        text-align: center;
        display: block;
        font-size: 15rem;
        line-height: 20rem;
        color: var(--surfaces-outline);
        filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
        opacity: 0.2;
      }
    }
  }

  &__sort {
    margin-bottom: 16px;

    &:deep(.cselect) {
      width: max-content;

      .cselect_top {
        min-width: 192px;
      }
    }
  }

  &__info {
    &-title {
      font-size: 28px;
      font-weight: 600;
      line-height: 36px;
      margin-bottom: 20px;
      color: var(--surfaces-on-surface);
    }

    &-desc {
      max-width: 599px;
      font-size: 16px;
      font-weight: 400;
      line-height: 32px;
      color: var(--surfaces-on-surface-variable);
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 16px;
  }

  @media (max-width: 525px) {
    padding-bottom: 16px;
    &__top-wrapper {
      flex-direction: column;
      gap: 8px;
      margin-bottom: 24px;

      .image-wrapper {
        width: 230px;
        height: 230px;
      }
    }

    &__info {
      &-title {
        font-size: 24px;
        line-height: 32px;
        margin-bottom: 12px;
      }

      &-desc {
        font-size: 14px;
        font-weight: 400;
        line-height: 28px;
      }
    }

    &__sort {
      .light {
        display: none;
      }

      &:deep(.cselect) {
        width: 100%;
      }
    }
    &__grid {
      gap: 12px;
      grid-template-columns: repeat(auto-fill, minmax(138px, 1fr));
    }
  }
}
</style>
